import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  MdNotifications,
  MdAccountCircle,
  MdDarkMode,
  MdLightMode,
  MdLogout,
  MdKeyboardArrowDown,
  MdMenu,
} from "react-icons/md";
import { IoMdSettings } from "react-icons/io";

import { logoutUser } from "../redux/actions/authActions";
import { toggleSidebar } from "../../store/slices/uiSlice";
import { GiHamburgerMenu } from "react-icons/gi";
import LogoutModal from "../modals/LogoutModal";
import io from "socket.io-client";
import { toast } from "react-toastify";
import { IoPersonSharp } from "react-icons/io5";

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sidebarCollapsed } = useSelector((state) => state.ui || {});
  const { user } = useSelector((state) => state.auth || {});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] =
    useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [socket, setSocket] = useState(null);
  const API_SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const OEMPortalUrl = process.env.REACT_APP_OEM_PORTAL_URL;
  const ALMATA_PORTAL_URL = process.env.REACT_APP_ALMATA_PORTAL_URL;
  const IT_SALES_PORTAL_URL = process.env.REACT_APP_ITSALES_PORTAL_URL;
  const getPageTitle = () => {
    switch (location.pathname) {
      case "/":
        return "Dashboard";
      case "/oem-partners":
        return "Onboarding OEM Partners";
      case "/almata-partners":
        return "Almata Partners";
      case "/revenue":
        return "Revenue";
      case "/transactions":
        return "Transaction";
      case "/scan":
        return "My Scan";
      case "/OEMScan":
        return "OEM Scan";
      case "/all-coupons":
        return "Coupons";
      case "/profile":
        return "Profile";
      case "/settings":
        return "Setting";
      case "/itsales-Partner":
        return "IT-Sales-Partner";
      case "/itoemdetails":
        return "IT-Sales-Partner > OEM";
      case "/it-end-user-of-oem":
        return "IT-Sales-Partner > OEM > EndUser";
      case "/almataOemPartner":
        return "Almata > OemPartner";
      case "/almataEndUserOfOem":
        return "Almata > OemPartner > EndUser";
      default:
        return "Dashboard";
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setDropdownOpen(false);
  };

  const handleLogoutConfirm = () => {
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }
    setNotifications([]);
    setNotificationCount(0);
    dispatch(logoutUser());
    setShowLogoutModal(false);
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const toggleSidebarHandler = () => {
    dispatch(toggleSidebar());
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotificationClick = () => {
    setNotificationDropdownOpen(!notificationDropdownOpen);
  };

  const handleNotificationItemClick = (notification, index) => {
    navigate(notification.route);
    setNotifications((prev) => prev.filter((_, i) => i !== index));
    setNotificationCount((prev) => (prev > 0 ? prev - 1 : 0));
    setNotificationDropdownOpen(false);
  };

  const getNotificationMessage = (notification) => {
    return (
      notification.message || "New Transaction has been done ! Please check."
    );
  };

  const showNotification = (title, message, type, route) => {
    toast.success(`${title}: ${message}`);
    setNotificationCount((prev) => {
      return prev + 1;
    });
    setNotifications((prev) => {
      const newNotification = {
        title,
        message,
        timestamp: new Date(),
        type,
        route,
      };
      return [newNotification, ...prev];
    });
  };

  useEffect(() => {
    if (user?._id) {
      const socketInstance = io(API_SOCKET_URL || "https://riskreport.almata.co.jp:3000", {
        path: "/socket.io/",
        transports: ["websocket", "polling"],
      });

      socketInstance.on("connect", () => {
        socketInstance.emit("joinUserRoom", user._id);
      });

      socketInstance.on("disconnect", (reason) => {
        console.log("❌ Admin socket disconnected:", reason);
      });

      socketInstance.on("scanStarted", (data) => {
        showNotification(
          "Scan Started",
          data.message,
          "scanStarted",
          "/OEMScan"
        );
      });

      socketInstance.on("scanCompleted", (data) => {
        showNotification(
          "Scan Completed",
          data.message,
          "scanCompleted",
          "/OEMScan"
        );
      });

      socketInstance.on("userVerified", (data) => {
        showNotification(
          "User Verified",
          data.message,
          "userVerified",
          "/transactions"
        );
      });

      setSocket(socketInstance);

      return () => {
        socketInstance.disconnect();
      };
    } else {
      console.log("❌ No admin user ID found, cannot create socket");
    }
  }, [user?._id]);

  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [socket]);

  const handleProfilePage = () => {
    navigate("/profile");
    setDropdownOpen(false);
  };

  const handleSettingClick = () => {
    navigate("/settings");
    setDropdownOpen(false);
  };
  const handleShareLinkUrl = () => {
    const newWindow = window.open('', '_blank');
    newWindow.document.write(`
      <html>
        <head><title>Redirecting...</title></head>
        <body>
          <script>
            // Clear all storage
            localStorage.clear();
            sessionStorage.clear();
            // Clear cookies for the domain
            document.cookie.split(";").forEach(function(c) { 
              document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
            });
            // Redirect to login
            window.location.href = '${OEMPortalUrl}/login';
          </script>
        </body>
      </html>
    `);
  };
  const handleShareLinkUrlAlmata = () => {
    const newWindow = window.open('', '_blank');
    newWindow.document.write(`
      <html>
        <head><title>Redirecting...</title></head>
        <body>
          <script>
            // Clear all storage
            localStorage.clear();
            sessionStorage.clear();
            // Clear cookies for the domain
            document.cookie.split(";").forEach(function(c) { 
              document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
            });
            // Redirect to login
            window.location.href = '${ALMATA_PORTAL_URL}/login';
          </script>
        </body>
      </html>
    `);
  };
  const handleShareLinkUrlItsales = () => {
    const newWindow = window.open('', '_blank');
    newWindow.document.write(`
      <html>
        <head><title>Redirecting...</title></head>
        <body>
          <script>
            // Clear all storage
            localStorage.clear();
            sessionStorage.clear();
            // Clear cookies for the domain
            document.cookie.split(";").forEach(function(c) { 
              document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
            });
            // Redirect to login
            window.location.href = '${IT_SALES_PORTAL_URL}/login';
          </script>
        </body>
      </html>
    `);
  };
  function capitalizeFirst(str = "") {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  return (
    <header
      className={`header ${
        location.pathname === "/cyberRiskReport" &&
        "/cyberRiskReport/verify-mail/:token"
          ? "d-none"
          : ""
      }`}
    >
      <div className="header-left">
        <div className="header-toggle-btn" onClick={toggleSidebarHandler}>
          <GiHamburgerMenu />
        </div>
        <h1 className="page-title">{getPageTitle()}</h1>
      </div>
      <div className="header-right">
        <div className="header-actions">
          <div>
            <button
              type="submit"
              className="add-btn"
              onClick={handleShareLinkUrlAlmata}
            >
              Almata Url
            </button>
          </div>

          <div>
            <button
              type="submit"
              className="add-btn"
              onClick={handleShareLinkUrlItsales}
            >
              It Sales Url
            </button>
          </div>
          <div>
            <button
              type="submit"
              className="add-btn"
              onClick={handleShareLinkUrl}
            >
              OEM Url
            </button>
          </div>
          <div className="notification-dropdown" ref={notificationRef}>
            <div className="notification-btn" onClick={handleNotificationClick}>
              <MdNotifications />
              {notificationCount > 0 && (
                <span className="notification-badge">{notificationCount}</span>
              )}
            </div>
            {notificationDropdownOpen && (
              <div
                className="dropdown-menu"
                style={{
                  display: "block",
                  width: "300px",
                  maxHeight: "300px",
                  padding: "0",
                  overflowY: "auto",
                }}
              >
                <div
                  className="dropdown-item d-flex justify-content-center"
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    textAlign: "center",
                    fontWeight: "bold",
                    borderRadius: "10px 10px 0 0",
                  }}
                >
                  Notifications
                </div>
                {notifications.length === 0 ? (
                  <div className="dropdown-item d-flex justify-content-center">
                    No notifications
                  </div>
                ) : (
                  <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                    {notifications.map((notification, index) => (
                      <div
                        key={index}
                        className="dropdown-item"
                        onClick={() =>
                          handleNotificationItemClick(notification, index)
                        }
                        style={{
                          cursor: "pointer",
                          borderBottom: "1px solid #eee",
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                          padding: "10px 15px",
                          lineHeight: "1.4",
                          overflowWrap: "break-word",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px",
                        }}
                      >
                        <span className="notification_active"></span>
                        <span style={{ flex: 1 }}>
                          {getNotificationMessage(notification)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="profile-dropdown" ref={dropdownRef}>
            <div
              className="profile-btn"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {/* <MdAccountCircle /> */}
              <span className="user-name-header">
                {capitalizeFirst(user?.firstName || user?.name || "admin")}
              </span>
              <MdKeyboardArrowDown
                className={`dropdown-arrow ${dropdownOpen ? "open" : ""}`}
              />
            </div>
            {dropdownOpen && (
              <div className="dropdown-menu" style={{ display: "block" }}>
                <button className="dropdown-item" onClick={handleProfilePage}>
                  <IoPersonSharp />
                  Profile
                </button>
                <button className="dropdown-item" onClick={handleSettingClick}>
                  <IoMdSettings />
                  Setting
                </button>
                <button
                  className="dropdown-item logout"
                  onClick={handleLogoutClick}
                >
                  <MdLogout />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
      />
    </header>
  );
};

export default Header;
